import React, { useState, useEffect } from "react";
import { es } from "date-fns/locale";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  TextField,
  FormControl,
  FormHelperText,
  Typography,
} from "@mui/material";
import clsx from "clsx";
import { IsDesktopHandler, dateConvert } from "../../../helpers";
import errorMessage from "../../../common/errorMessages.json";
import { Controller } from "react-hook-form";
import { makeStyles } from "@mui/styles";
import theme from "../../../theme";
import "./TextFieldWidgetDateRange.scss";
import { format } from "date-fns";

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: 0,
    minWidth: 180,
    width: "100%",
    maxWidth: "100%",
    fontSize: 14,
    color: theme.palette.text.primary,
  },
  rootLabel: {
    fontSize: 14,
    paddingLeft: 5,
    paddingRight: 5,
    color: theme.palette.primary.main,
    fontWeight: 500,
    marginBottom: "8px",
  },
  inputContainerStyleVertical: (props) => ({
    minHeight: props.minheight ? 65 : "auto",
    ...props.containerStyle,
    "& .MuiTextField-root": {
      marginTop: "5px",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.borderRadius,
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: (props) => (props.sizeSelect === "large" ? "16px" : "16px"),
      color: "#4C5662 !important",
    },
    [theme.breakpoints.down("sm")]: {
      minWidth: "100%",
    },
  }),
  dateField: {
    "& .MuiInputBase-input, & .MuiOutlinedInput-input": {
      padding: (props) =>
        props.sizeSelect === "large"
          ? "12px 14px !important"
          : "10px 14px !important",
      fontSize: "14px",
    },
    "& .MuiInputBase-input.MuiOutlinedInput-input": {
      padding: (props) =>
        props.sizeSelect === "large"
          ? "12px 14px !important"
          : "10px 14px !important",
    },
    "& .MuiOutlinedInput-root": {
      minHeight: (props) => (props.sizeSelect === "large" ? "48px" : "40px"),
      "& input::placeholder": {
        fontSize: "14px",
        opacity: 0.6,
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: "14px",
    },
  },
}));

const TextFieldWidgetDateRange = (props) => {
  const {
    errors,
    name,
    labeltext,
    value = [null, null],
    onChange,
    labelFunc,
    emptyLabel,
    autoOk,
    onOpen,
    onClose,
    open: openForward,
    beginSinceDate,
    beginUntilDate,
    req,
    loading,
    disabled,
    display = "vertical",
    variant = "outlined",
    minheight = true,
    containerStyle,
    sizeSelect = "medium",
    style,
    ...others
  } = props;

  const classesUI = useStyles({ minheight, containerStyle, sizeSelect });
  const [begin, setBegin] = useState(value[0]);
  const [end, setEnd] = useState(value[1]);
  const [stateMessage, setStateMessage] = useState("");
  const [stateError, setStateError] = useState(false);
  const [prevBegin, setPrevBegin] = useState(undefined);
  const [prevEnd, setPrevEnd] = useState(undefined);
  const [hasClicked, setHasClicked] = useState(false);
  const [hover, setHover] = useState(undefined);
  const [accepted, setAccepted] = useState(false);
  const [open, setOpen] = useState(false);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isOpen = openForward !== undefined ? openForward : open;

  useEffect(() => {
    if (errors) {
      switch (errors.type) {
        case "required":
          const fieldError = errorMessage.message[name];
          if (fieldError) {
            setStateMessage(fieldError.required || fieldError);
          } else {
            setStateMessage("Este campo es requerido");
          }
          break;
        default:
          break;
      }
      setStateError(true);
    } else {
      setStateMessage("");
      setStateError(false);
    }
  }, [errors, name]);

  useEffect(() => {
    if (beginSinceDate && beginUntilDate && !accepted) {
      setPrevBegin(dateConvert(beginSinceDate));
      setPrevEnd(dateConvert(beginUntilDate));
    }
  }, [beginSinceDate, beginUntilDate, accepted]);

  const handleDateChange = (newValue, field) => {
    setBegin(newValue[0]);
    setEnd(newValue[1]);

    // Actualizar el valor del formulario con el formato correcto
    const dateRange = { begin: newValue[0], end: newValue[1] };
    field.onChange(dateRange);

    if (onChange) {
      onChange(dateRange);
    }
  };

  let labelText = props.labeltext || null;
  const isDesktop = IsDesktopHandler("sm");

  // CustomDateContainer similar al patrón de SelectMultipleWidgetFilter
  const CustomDateContainer = (props) => {
    return (
      <div className={clsx(classesUI.inputContainerStyleVertical)}>
        {props.children}
      </div>
    );
  };

  return (
    <CustomDateContainer>
      {loading && display !== "vertical" && <p>cargando...</p>}
      <FormControl
        className={classesUI.formControl}
        error={stateError}
        variant="standard"
        style={style || {}}
      >
        {labelText && (
          <Typography className={classesUI.rootLabel}>{labeltext}</Typography>
        )}

        <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
          <Controller
            name={name}
            control={props.control}
            defaultValue={{ begin: null, end: null }}
            rules={{
              required: req,
            }}
            render={({ field }) => (
              <div
                className={clsx(
                  "custom-date-range-container",
                  sizeSelect === "large" ? "size-large" : "size-medium",
                  display === "horizontal" ? "flex gap-4" : ""
                )}
              >
                <DatePicker
                  label="Fecha de inicio"
                  value={begin}
                  minDate={beginSinceDate || today}
                  format="dd/MM/yyyy"
                  onChange={(newValue) => {
                    setBegin(newValue);
                    handleDateChange([newValue, end], field);
                  }}
                  disabled={disabled}
                  slots={{
                    textField: TextField,
                  }}
                  slotProps={{
                    textField: {
                      variant: variant,
                      fullWidth: true,
                      margin: "none",
                      error: stateError,
                      helperText: stateError ? stateMessage : "",
                      className: clsx(classesUI.dateField, "date-range-input"),
                      InputLabelProps: { shrink: true },
                    },
                  }}
                  {...others}
                />
                <DatePicker
                  label="Fecha de fin"
                  value={end}
                  minDate={begin || today} // Fecha mínima es la fecha de inicio o hoy
                  format="dd/MM/yyyy"
                  onChange={(newValue) => {
                    setEnd(newValue);
                    handleDateChange([begin, newValue], field);
                  }}
                  disabled={disabled}
                  slots={{
                    textField: TextField,
                  }}
                  slotProps={{
                    textField: {
                      variant: variant,
                      fullWidth: true,
                      margin: "none",
                      error: stateError,
                      className: clsx(classesUI.dateField, "date-range-input"),
                      InputLabelProps: { shrink: true },
                    },
                  }}
                  {...others}
                />
              </div>
            )}
          />
        </LocalizationProvider>

        {stateError && <FormHelperText error>{stateMessage}</FormHelperText>}
      </FormControl>
    </CustomDateContainer>
  );
};

export default TextFieldWidgetDateRange;
